import React from 'react';
import { Button, Stack } from '@mui/material';
import { WaiverOptionType } from '@prisma/client';
import { WaiverOption } from '../../../types/waivers';
import { SortableList } from '../../dnd/SortableList';
import OptionsListItem from '../OptionsListItem';
import CheckboxOption from '../CheckboxOption';
import TextSignatureOption from '../TextSignatureOption';
import useAwaitedForm from '../../../hooks/useAwaitedForm';
import { convertSnakeToTitleCase } from '../../../utils/global/helper';
import useConfirm from '../../../hooks/useConfirm';

interface Props {
  options: WaiverOption[];
  onChange: (newOptions: WaiverOption[]) => void;
  disabled?: boolean;
  placeholder: string;
}

function WaiverSortableListItem({
  item,
  onChange,
  options,
  placeholder = 'Type an option',
  disabled,
}: Props & {
  item: WaiverOption;
}) {
  return (
    <OptionsListItem
      isLargeItem={item.type !== 'CHECKBOX'}
      options={options}
      item={item}
      disabled={disabled}
      onChange={onChange}>
      {item.type === 'CHECKBOX' ? (
        <CheckboxOption
          disabled={disabled}
          item={item}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
        />
      ) : (
        <TextSignatureOption
          disabled={disabled}
          item={item}
          options={options}
          onChange={onChange}
        />
      )}
    </OptionsListItem>
  );
}

function OptionsList({
  options,
  onChange,
  placeholder,
  disabled,
  addText = 'Add option',
  combineAllOptions = false,
  askForType = false,
  allowedTypes = ['CHECKBOX', 'TEXT', 'MULTILINE_TEXT', 'SIGNATURE'],
  warnOn18 = false,
}: Props & {
  addText?: string;
  combineAllOptions?: boolean;
  askForType?: boolean;
  allowedTypes?: WaiverOptionType[];
  warnOn18?: boolean;
}) {
  const awaitedForm = useAwaitedForm();
  const confirm = useConfirm();
  const warnedOptionIdsRef = React.useRef<Set<string>>(new Set());

  const checkboxOptions = React.useMemo(
    () => options.filter(option => option.type === 'CHECKBOX'),
    [options],
  );

  const nonCheckboxOptions = React.useMemo(
    () =>
      options.filter(
        option =>
          option.type === 'TEXT' ||
          option.type === 'MULTILINE_TEXT' ||
          option.type === 'SIGNATURE',
      ),
    [options],
  );

  const combinedOptions = React.useMemo(
    () => (combineAllOptions ? options : checkboxOptions),
    [combineAllOptions, options, checkboxOptions],
  );

  const onChangeOptions = React.useCallback(
    async (newOptions: WaiverOption[]) => {
      // Check if warnOn18 is enabled and if any option contains "18"
      if (warnOn18) {
        // Find options that contain "18" and haven't been warned about yet
        const optionsToWarn = newOptions.filter(
          option =>
            option.text?.toLowerCase().includes('18') &&
            !warnedOptionIdsRef.current.has(option.id),
        );

        if (optionsToWarn.length > 0) {
          // Mark these options as warned
          optionsToWarn.forEach(option => {
            warnedOptionIdsRef.current.add(option.id);
          });

          await confirm({
            title: 'Age Confirmation Warning',
            message:
              "If the client filling out the waiver is not a minor, they'll automatically be asked to confirm their age and that they are over 18 years of age. If they are a minor, they'll see the Minor Addendum instead. You should not add a required acknowledgment option for age confirmation.",
            positiveText: 'OK',
            negativeText: undefined,
          });
        }
      }

      // keep non-sorted options at the end
      const uncombinedOptions = combineAllOptions ? [] : nonCheckboxOptions;
      const newList = [...newOptions, ...uncombinedOptions].map(
        (option, index) => ({
          ...option,
          order: index + 1,
        }),
      );
      onChange(newList);
    },
    [combineAllOptions, nonCheckboxOptions, onChange, warnOn18, confirm],
  );

  const renderSortableItem = React.useCallback(
    (item: WaiverOption) => (
      <WaiverSortableListItem
        item={item}
        onChange={onChangeOptions}
        options={combinedOptions}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [onChangeOptions, combinedOptions, placeholder, disabled],
  );

  const renderTextSignatureItem = React.useCallback(
    (option: WaiverOption) => (
      <TextSignatureOption
        item={option}
        options={options}
        onChange={combineAllOptions ? onChangeOptions : onChange}
        disabled={disabled}
      />
    ),
    [options, combineAllOptions, onChangeOptions, onChange, disabled],
  );

  return (
    <Stack boxSizing="border-box">
      <SortableList
        items={combinedOptions}
        onChange={newOptions => {
          onChangeOptions(newOptions);
        }}
        renderItem={renderSortableItem}
      />
      <Button
        disabled={disabled}
        type="button"
        variant="smallLink"
        sx={{
          paddingLeft: 2,
          textAlign: 'left',
          width: 'fit-content',
        }}
        onClick={async () => {
          const type = askForType
            ? (
                await awaitedForm({
                  form: {
                    title: 'Add Question',
                    submitButtonText: 'Add',
                    initialValues: {
                      type: 'CHECKBOX' as WaiverOptionType,
                    },
                    inputRows: [
                      [
                        {
                          inputType: 'radio-select',
                          label: 'Input type',
                          name: 'type',
                          options: allowedTypes.map(t => ({
                            label: convertSnakeToTitleCase(t),
                            value: t,
                          })),
                        },
                      ],
                    ],
                  },
                  allowUntouched: true,
                })
              )?.type
            : 'CHECKBOX';

          if (!type) return;

          // only used locally
          const randomID = Math.random().toString(36).substring(7);
          onChangeOptions([
            ...combinedOptions,
            {
              id: randomID,
              text: '',
              order: combinedOptions.length + 1,
              type,
            },
          ]);
        }}>
        <span>{addText}</span>
      </Button>
      {!!nonCheckboxOptions?.length && !combineAllOptions && (
        <Stack mt={5} spacing={2} boxSizing="border-box">
          {nonCheckboxOptions.map(renderTextSignatureItem)}
        </Stack>
      )}
    </Stack>
  );
}

export default OptionsList;
